import { useEffect } from "react";
import {
  useForm,
  useFieldArray,
  FieldPath,
  UseFormSetError,
} from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import toast from "react-hot-toast";
import { fr } from "yup-locales";
import { setLocale } from "yup";
import {
  cleanerError,
  normalizeDecimalInput,
  parseMoneyToCents,
} from "@/lib/utils";
import { getApiErrorMessage, translateApiErrorText } from "@/lib/apiErrors";
import { useBulkCreatePurchaseOrdersMutation } from "@/redux/services/inventory/purchaseOrder.api";
import { PurchaseOrder } from "@/redux/services/inventory/inventory.type";

setLocale(fr);

const QUANTITY_REGEX = /^\d+(\.\d{1,6})?$/;
const PRICE_REGEX = /^\d+(\.\d{1,2})?$/;

export const MAX_BULK_ORDERS = 50;

interface Violation {
  propertyPath: string;
  message: string;
}

// Backend validation keys → localized copy (see bulk-purchase API contract)
const VIOLATION_MESSAGES: Record<string, string> = {
  "validation.bulk_purchase.error.orders.not_empty":
    "Ajoutez au moins un fournisseur.",
  "validation.bulk_purchase.error.orders.too_many": `Maximum ${MAX_BULK_ORDERS} fournisseurs par enregistrement.`,
  "validation.bulk_purchase.error.supplier.not_in_restaurant":
    "Ce fournisseur n'appartient pas à ce restaurant.",
  "validation.bulk_purchase.error.ingredient.not_in_restaurant":
    "Cet ingrédient n'appartient pas à ce restaurant.",
  "validation.purchase_order.error.lines.not_empty":
    "Ajoutez au moins une ligne.",
  "validation.purchase_order_item.error.quantity_ordered.positive":
    "La quantité doit être supérieure à 0.",
  "validation.purchase_order_item.error.unit_price.not_negative":
    "Le prix unitaire ne peut pas être négatif.",
  "validation.purchase_order_item.error.packaging.ingredient_mismatch":
    "Ce conditionnement n'appartient pas à cet ingrédient.",
  "validation.purchase_order_item.error.package_quantity_ordered.positive":
    "La quantité de conditionnements doit être supérieure à 0.",
  "validation.purchase_order_item.error.package_unit_price.not_negative":
    "Le prix par conditionnement ne peut pas être négatif.",
};

// Context wording first, then the shared map; never the raw key.
const translateViolation = (key: string): string =>
  translateApiErrorText(key, VIOLATION_MESSAGES) ?? "Valeur invalide.";

// Map a server propertyPath (e.g. "orders[0].lines[1].packageUnitPrice")
// onto the matching react-hook-form field path. Packaging lines reuse the
// form's quantityOrdered/unitPrice inputs, and packaging errors surface on
// the ingredient field (the packaging selector sits in that cell).
const toFormFieldPath = (
  propertyPath: string
): FieldPath<BulkPurchaseOrderFormData> =>
  propertyPath
    .replace(/\[(\d+)\]/g, ".$1")
    .replace(/\.packageQuantityOrdered$/, ".quantityOrdered")
    .replace(/\.packageUnitPrice$/, ".unitPrice")
    .replace(/\.packaging$/, ".ingredient") as FieldPath<BulkPurchaseOrderFormData>;

const applyViolations = (
  violations: Violation[],
  setError: UseFormSetError<BulkPurchaseOrderFormData>
): string[] => {
  const unmapped: string[] = [];
  for (const v of violations) {
    const message = translateViolation(v.message);
    if (v.propertyPath) {
      setError(toFormFieldPath(v.propertyPath), { type: "server", message });
    } else {
      unmapped.push(message);
    }
  }
  return unmapped;
};

export interface PurchaseOrderFormLine {
  ingredient: string;
  packaging?: string;
  quantityOrdered: string;
  unitPrice: string;
}

export interface BulkPurchaseOrderFormOrder {
  supplier: string;
  orderDate?: string;
  expectedDeliveryDate?: string;
  notes?: string;
  lines: PurchaseOrderFormLine[];
}

export interface BulkPurchaseOrderFormData {
  orders: BulkPurchaseOrderFormOrder[];
}

/**
 * Line total in cents as typed. Accepts comma decimals ("2,5"); an empty or
 * unreadable quantity/price counts as 0.
 */
export const bulkLineTotalCents = (
  line: Partial<PurchaseOrderFormLine> | undefined
): number => {
  const qty =
    parseFloat(normalizeDecimalInput(line?.quantityOrdered ?? "") ?? "") || 0;
  const unitCents = parseMoneyToCents(line?.unitPrice) ?? 0;
  return qty * unitCents;
};

export const bulkOrderTotalCents = (
  order: Partial<BulkPurchaseOrderFormOrder> | undefined
): number =>
  (order?.lines ?? []).reduce((sum, line) => sum + bulkLineTotalCents(line), 0);

export const bulkGrandTotalCents = (
  orders: (Partial<BulkPurchaseOrderFormOrder> | undefined)[]
): number =>
  orders.reduce((acc, order) => acc + bulkOrderTotalCents(order), 0);

const todayISO = () => new Date().toISOString().slice(0, 10);

export const emptyOrderLine = (): PurchaseOrderFormLine => ({
  ingredient: "",
  packaging: "",
  quantityOrdered: "",
  unitPrice: "",
});

export const emptyOrder = (): BulkPurchaseOrderFormOrder => ({
  supplier: "",
  orderDate: todayISO(),
  expectedDeliveryDate: "",
  notes: "",
  lines: [emptyOrderLine()],
});

const lineSchema = yup.object({
  ingredient: yup.string().required().label("Ingrédient"),
  packaging: yup.string().optional().label("Conditionnement"),
  quantityOrdered: yup
    .string()
    .required()
    .test(
      "is-positive",
      "Quantité doit être > 0 (max 6 décimales)",
      (v) => {
        // "1,5" (French keypad) is read as 1.5, not rejected.
        const normalized = normalizeDecimalInput(v ?? "");
        return (
          normalized !== null &&
          QUANTITY_REGEX.test(normalized) &&
          parseFloat(normalized) > 0
        );
      }
    )
    .label("Quantité"),
  unitPrice: yup
    .string()
    .required()
    .test(
      "is-positive",
      "Prix doit être un nombre positif (max 2 décimales)",
      (v) => {
        const normalized = normalizeDecimalInput(v ?? "");
        return (
          normalized !== null &&
          PRICE_REGEX.test(normalized) &&
          parseFloat(normalized) > 0
        );
      }
    )
    .label("Prix unitaire"),
});

export const bulkPurchaseOrderValidationSchema: yup.ObjectSchema<BulkPurchaseOrderFormData> =
  yup.object().shape({
    orders: yup
      .array()
      .of(
        yup.object({
          supplier: yup.string().required().label("Fournisseur"),
          orderDate: yup.string().optional().label("Date d'achat"),
          expectedDeliveryDate: yup
            .string()
            .optional()
            .label("Livraison prévue"),
          notes: yup.string().optional().max(1000).label("Notes"),
          lines: yup
            .array()
            .of(lineSchema)
            .min(1, "Ajoutez au moins une ligne")
            .required()
            .label("Lignes"),
        })
      )
      .min(1, "Ajoutez au moins un fournisseur")
      .max(
        MAX_BULK_ORDERS,
        `Maximum ${MAX_BULK_ORDERS} fournisseurs par enregistrement`
      )
      .required()
      .label("Commandes"),
  });

export const useBulkPurchaseOrders = (
  restaurantId: string,
  onSuccess: (orders: PurchaseOrder[]) => void
) => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    reset,
    clearErrors,
    setError,
    watch,
    setValue,
  } = useForm<BulkPurchaseOrderFormData>({
    resolver: yupResolver(bulkPurchaseOrderValidationSchema),
    defaultValues: { orders: [emptyOrder()] },
  });

  const {
    fields: orderFields,
    append: appendOrder,
    remove: removeOrder,
  } = useFieldArray({ control, name: "orders" });

  const [bulkCreate, { isLoading }] = useBulkCreatePurchaseOrdersMutation();

  useEffect(() => {
    if (errors) cleanerError(errors, clearErrors);
  }, [errors, clearErrors]);

  const onSubmit = async (data: BulkPurchaseOrderFormData) => {
    const payload = {
      orders: data.orders.map((order) => ({
        supplier: `/api/suppliers/${order.supplier}`,
        ...(order.orderDate ? { orderDate: order.orderDate } : {}),
        ...(order.expectedDeliveryDate
          ? { expectedDeliveryDate: order.expectedDeliveryDate }
          : {}),
        ...(order.notes?.trim() ? { notes: order.notes.trim() } : {}),
        lines: order.lines.map((line) => {
          const ingredientIri = `/api/restaurants/${restaurantId}/ingredients/${line.ingredient}`;
          const unitPriceCents = parseMoneyToCents(line.unitPrice) ?? 0;
          const quantity =
            normalizeDecimalInput(line.quantityOrdered) ?? line.quantityOrdered;
          if (line.packaging) {
            return {
              ingredient: ingredientIri,
              packaging: `/api/ingredient_packagings/${line.packaging}`,
              packageQuantityOrdered: quantity,
              packageUnitPrice: unitPriceCents,
            };
          }
          return {
            ingredient: ingredientIri,
            quantityOrdered: quantity,
            unitPrice: unitPriceCents,
          };
        }),
      })),
    };

    const res = await bulkCreate({ restaurantId, data: payload });

    if ("data" in res && res.data) {
      const created = res.data;
      toast.success(
        created.length > 1
          ? `${created.length} bons de commande créés et reçus`
          : `Bon de commande ${created[0]?.poNumber ?? ""} créé et reçu`
      );
      reset();
      onSuccess(created);
      return;
    }

    // All-or-nothing: a 422 carries violations[] pinpointing each bad field.
    const error = "error" in res ? res.error : undefined;
    const errData = (
      error as
        | { data?: { violations?: Violation[]; detail?: string } }
        | undefined
    )?.data;
    const violations = errData?.violations;

    if (violations?.length) {
      const unmapped = applyViolations(violations, setError);
      toast.error(
        unmapped[0] ?? "Certaines lignes sont invalides — corrigez et réessayez."
      );
      return;
    }

    toast.error(
      getApiErrorMessage(error, "Erreur lors de la création", {
        forbidden: "Vous n'avez pas l'autorisation d'enregistrer des achats.",
      })
    );
  };

  return {
    register,
    control,
    errors,
    orderFields,
    appendOrder,
    removeOrder,
    watch,
    setValue,
    onSubmit: handleSubmit(onSubmit),
    isLoading,
  };
};
